
import React, { useState, useEffect } from "react";
import {
  Eye,
  Wallet,
  AlertCircle,
  CheckCircle,
  Check,
  Minus,
  Plus,
  UserX,
  RotateCcw,
  Printer,
  Loader2,
  Building2,
} from "lucide-react";

import StatusBadge from "./StatusBadge";
import EmployeeCardHeader from "../../../../components/EmployeeCardHeader";
import { getThHeaderStyle, getTdStyle } from "../../../(Admin)Attendance/styles/attendanceStyles";
import { useVisibleColumns } from "../../../../hooks/useVisibleColumns";
import Pagination from "../../../../components/Pagination";
import { downloadEmployeePayslipPdf } from "../../../(User)salary_Final/utils/downloadPayslip";

const PayrollTable = ({
  t,
  styles,
  theme,
  language,
  currentTheme,
  fonts,
  spacing,
  screenSize,
  currentItems,
  hasMore,
  visibleItems,
  filteredPayrollData,
  togglingIgnoredIds,
  unpayingIds,
  modulePermissions,
  hasRowActions,
  setSelectedEmployee,

  setDeductionModal,
  setAllowanceModal,
  handleToggleIsIgnored,

  handleUnpayPayroll,
  handleShowMore,
  setDeductionTooltip,
  showAlert,
  profile,
  selectionEnabled,
  selectedIds,
  onToggleSelect,
  allSelected,
  someSelected,
  onToggleSelectAll,
  // Server-side pagination: when these are given, the rows passed in ARE the
  // current page and page changes are requested from the server.
  serverPage,
  serverTotalPages,
  onServerPageChange,
}) => {
  const isAdmin = profile?.role === "admin" || profile?.role === "superadmin";

  // ✅ Admin-level column visibility (hiddenColumns registry). Bypassed roles
  // (admin/superadmin/managers with allModulesAllowed) always see every
  // column — the hook handles that, so their view is unchanged.
  const payrollColumns = [
    { key: "employee" },
    { key: "basicSalary" },
    { key: "allowances" },
    { key: "grossSalary" },
    { key: "deductions" },
    { key: "netSalary" },
    { key: "status" },
    { key: "actions" },
  ];
  const visibleColumns = useVisibleColumns("payroll", "payrollTable", payrollColumns);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  const serverDriven = typeof serverTotalPages === "number" && typeof onServerPageChange === "function";
  const [localPage, setLocalPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!serverDriven) setLocalPage(1);
  }, [filteredPayrollData, serverDriven]);

  const currentPage = serverDriven ? serverPage || 1 : localPage;
  const setCurrentPage = serverDriven ? onServerPageChange : setLocalPage;
  const totalPages = serverDriven
    ? serverTotalPages
    : Math.ceil((filteredPayrollData?.length || 0) / itemsPerPage);
  const startIdx = (localPage - 1) * itemsPerPage;
  const displayedPayroll = serverDriven
    ? filteredPayrollData || []
    : (filteredPayrollData || []).slice(startIdx, startIdx + itemsPerPage);

  // Print payslip (per row): generates that ONE employee's payslip PDF —
  // the same PrintablePayslip the employee sees on their own salary page.
  // Tracked per payroll id so only the clicked row shows a spinner.
  const [printingIds, setPrintingIds] = useState(() => new Set());
  const handlePrintPayslip = async (employee) => {
    if (printingIds.has(employee.id)) return;
    setPrintingIds((prev) => new Set(prev).add(employee.id));
    try {
      await downloadEmployeePayslipPdf({ payrollId: employee.id, language });
    } catch (err) {
      console.error("Print payslip failed:", err);
      showAlert?.(
        t("error"),
        language === "ar" ? "تعذر إنشاء كشف المرتب" : "Could not generate the payslip",
        "error",
      );
    } finally {
      setPrintingIds((prev) => {
        const next = new Set(prev);
        next.delete(employee.id);
        return next;
      });
    }
  };

  const showRowActions =
    isAdmin ||
    (hasRowActions &&
      (modulePermissions?.employeeDetails ||
        modulePermissions?.employeeDeductions ||
        modulePermissions?.employeeAllowances ||
        modulePermissions?.ignoreAttendance ||
        (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll)));

  const renderIgnoreAttendanceButton = (employee) => {
    const isToggling = togglingIgnoredIds.has(employee.id);
    const isIgnored = employee.isIgnored;

    return (
      <button
        onClick={() => handleToggleIsIgnored(employee.id)}
        disabled={isToggling}
        title={employee.isIgnored ? t("unignoreAttendance") : t("ignoreAttendance")}
        style={btnStyle({ theme, variant: "neutral", isIgnored, disabled: isToggling })}
        onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral", isIgnored, disabled: isToggling })}
        onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral", isIgnored, disabled: isToggling })}
      >
        <UserX size={14} />
      </button>
    );
  };

  const renderUnpayButton = (employee) => {
    const isUnpaying = unpayingIds.has(employee.id);
    const isPaid = employee.status === "paid";

    if (!isPaid) return null;

    const handleUnpayClick = () => {
      if (window.confirm(`${t("unpayConfirm")} (${employee.employeeName})`)) {
        handleUnpayPayroll(employee.id, {
          onSuccess: () => {
            if (showAlert) {
              showAlert(t("success"), t("unpaySuccess"), "success");
            }
          },
          onError: (err) => {
            if (showAlert) {
              showAlert(
                t("error"),
                t("unpayError") + ": " + err.message,
                "error",
              );
            }
          },
        });
      }
    };

    return (
      <button
        onClick={handleUnpayClick}
        disabled={isUnpaying}
        title={t("unpayPayroll")}
        style={btnStyle({ theme, variant: "neutral", disabled: isUnpaying })}
        onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral", disabled: isUnpaying })}
        onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral", disabled: isUnpaying })}
      >
        <RotateCcw size={14} />
      </button>
    );
  };


  // Row action buttons — shared by the desktop table and the phone cards so
  // both honour exactly the same permission gates.
  const renderRowActionButtons = (employee) => (
    <>
      {(isAdmin || modulePermissions?.employeeDetails) && (
        <button
          style={btnStyle({ theme, variant: "neutral" })}
          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
          onClick={() => setSelectedEmployee(employee)}
          title={t("viewDetails")}
        >
          <Eye size={14} />
        </button>
      )}
      {(isAdmin || modulePermissions?.employeeDeductions) && (
        <button
          style={btnStyle({ theme, variant: "neutral" })}
          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
          onClick={() => setDeductionModal({ isOpen: true, employee })}
          title={t("deductions")}
        >
          <Minus size={14} color="#ef4444" />
        </button>
      )}
      {(isAdmin || modulePermissions?.employeeAllowances) && (
        <button
          style={btnStyle({ theme, variant: "neutral" })}
          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
          onClick={() => setAllowanceModal({ isOpen: true, employee })}
          title={t("allowances")}
        >
          <Plus size={14} color="#10b981" />
        </button>
      )}
      {/* Same gate as "view details": the payslip shows the
          same salary breakdown the details modal does. */}
      {(isAdmin || modulePermissions?.employeeDetails) && (
        <button
          style={btnStyle({ theme, variant: "neutral", disabled: printingIds.has(employee.id) })}
          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral", disabled: printingIds.has(employee.id) })}
          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral", disabled: printingIds.has(employee.id) })}
          onClick={() => handlePrintPayslip(employee)}
          disabled={printingIds.has(employee.id)}
          title={language === "ar" ? "طباعة كشف المرتب" : "Print payslip"}
          aria-label={language === "ar" ? `طباعة كشف مرتب ${employee.employeeName}` : `Print payslip for ${employee.employeeName}`}
        >
          {printingIds.has(employee.id)
            ? <Loader2 size={14} className="animate-spin" />
            : <Printer size={14} color="#2e7cba" />}
        </button>
      )}
      {(modulePermissions?.ignoreAttendance || isAdmin) && renderIgnoreAttendanceButton(employee)}
      {/* Unpay is gated by the registry's unpayPayroll key
          (falls back to processPayroll on legacy permission
          payloads that predate the key). */}
      {(isAdmin || (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll)) && renderUnpayButton(employee)}
    </>
  );

  // Phones: the 8-column table becomes one card per employee (net salary up
  // front, the breakdown underneath) instead of a sideways-scrolling table.
  const isPhone = screenSize === "xs" || screenSize === "sm" || screenSize === "md";
  const cardColumns = screenSize === "md" ? "repeat(2, minmax(0, 1fr))" : "1fr";
  const renderSelectDot = (checked, mixed, onToggle, size = 22) => (
    <span
      onClick={onToggle}
      role="checkbox"
      aria-checked={checked ? "true" : mixed ? "mixed" : "false"}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onToggle?.(); } }}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        border: `2px solid ${checked || mixed ? currentTheme.colors.primary : (theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)")}`,
        background: checked ? currentTheme.colors.primary : mixed ? `${currentTheme.colors.primary}33` : "transparent",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {checked && <Check size={12} strokeWidth={3} color={theme === "dark" ? "#0b1220" : "#ffffff"} />}
      {mixed && !checked && <span style={{ width: "8px", height: "2px", borderRadius: "1px", background: currentTheme.colors.primary }} />}
    </span>
  );

  const renderMobileCards = () => {
    const muted = currentTheme.colors.textMuted;
    const cardBorder = theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(15,23,42,0.08)";
    const amount = (v) => Math.round(Number(v) || 0).toLocaleString();
    const breakdown = (emp) => [
      showColumn("basicSalary") && { label: t("basicSalary"), value: amount(emp.basicSalary), color: currentTheme.colors.textPrimary },
      showColumn("allowances") && { label: t("allowances"), value: `+${amount(emp.totalAllowances)}`, color: "#10b981" },
      showColumn("deductions") && { label: t("deductions"), value: `-${amount(emp.totalDeductions)}`, color: currentTheme.colors.danger },
      showColumn("grossSalary") && { label: t("grossSalary"), value: amount(emp.grossSalary), color: currentTheme.colors.textPrimary },
    ].filter(Boolean);

    return (
      <div className="nx-mcard-list" style={{ display: "grid", gridTemplateColumns: cardColumns, gap: "12px", alignItems: "start" }}>
        {selectionEnabled && displayedPayroll.length > 0 && (
          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "10px", padding: "4px 4px 2px", fontSize: "13px", color: muted }}>
            {renderSelectDot(allSelected, someSelected, onToggleSelectAll)}
            <span>{language === "ar" ? "تحديد الكل" : "Select all"}</span>
          </div>
        )}
        {displayedPayroll.map((employee) => {
          const selected = selectedIds?.has(employee.id) || false;
          const items = breakdown(employee);
          return (
            <article
              key={employee.id}
              style={{
                padding: "14px",
                borderRadius: "14px",
                background: currentTheme.colors.cardBackground,
                border: selected ? `1px solid ${currentTheme.colors.primary}` : cardBorder,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                direction: language === "ar" ? "rtl" : "ltr",
              }}
            >
              <EmployeeCardHeader
                theme={theme}
                leading={selectionEnabled && renderSelectDot(selected, false, () => onToggleSelect?.(employee.id))}
                name={showColumn("employee") ? employee.employeeName : ""}
                subtitle={[
                  employee.clientCode,
                  employee.paidDate && new Date(employee.paidDate).toLocaleDateString("en-US"),
                ].filter(Boolean).join(" · ")}
                email={showColumn("employee") ? employee.personalInfo?.email || employee.email || "" : ""}
                info={employee.department && employee.department !== "N/A" ? employee.department : ""}
                infoIcon={Building2}
                status={showColumn("status") ? <StatusBadge status={employee.status} language={language} t={t} /> : null}
              />

              {showColumn("netSalary") && (
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px" }}>
                  <span style={{ fontSize: "12.5px", color: muted }}>{t("netSalary")}</span>
                  <span style={{ fontSize: "20px", fontWeight: 700, color: currentTheme.colors.textPrimary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {amount(employee.netSalary)} <span style={{ fontSize: "12px", fontWeight: 500, color: muted }}>{t("currency")}</span>
                  </span>
                </div>
              )}

              {items.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${Math.min(items.length, 2)}, minmax(0, 1fr))`,
                    gap: "10px 12px",
                    paddingTop: "10px",
                    borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(15,23,42,0.06)",
                  }}
                >
                  {items.map((it) => (
                    <div key={it.label} style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                      <span style={{ fontSize: "12px", color: muted }}>{it.label}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: it.color, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{it.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {showColumn("actions") && showRowActions && (
                <div className="nx-payroll-card-actions" style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {renderRowActionButtons(employee)}
                </div>
              )}
            </article>
          );
        })}
        <div style={{ gridColumn: "1 / -1" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            theme={theme}
            isRTL={language === "ar"}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "700",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexDirection: "row",
          textAlign: language === "ar" ? "right" : "left",
          color: currentTheme.colors.textPrimary,
        }}
      >
        <Wallet size={20} />
        {language === "ar" ? "قائمة المرتبات" : "Payroll List"}
      </h3>
      {isPhone ? renderMobileCards() : (
      <div style={styles.tableContainer}>
        <table
          style={{
            ...styles.table,
            minWidth:
              screenSize === "xs"
                ? "600px"
                : screenSize === "sm"
                  ? "700px"
                  : "900px",
          }}
        >
          <thead>
            <tr style={styles.tableHeader}>
              {selectionEnabled && (
                <th style={{ ...styles.tableHeader, width: "36px", padding: "0.75rem 0.5rem", textAlign: "center" }}>
                  <span
                    onClick={onToggleSelectAll}
                    role="checkbox"
                    aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onToggleSelectAll?.(); } }}
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${allSelected || someSelected ? currentTheme.colors.primary : (theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)")}`,
                      background: allSelected ? currentTheme.colors.primary : someSelected ? `${currentTheme.colors.primary}33` : "transparent",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                    }}
                  >
                    {allSelected && (
                      <Check size={12} strokeWidth={3} color={theme === "dark" ? "#0b1220" : "#ffffff"} />
                    )}
                    {someSelected && !allSelected && (
                      <span style={{ width: "8px", height: "2px", borderRadius: "1px", background: currentTheme.colors.primary }} />
                    )}
                  </span>
                </th>
              )}
              {showColumn("employee") && (
                <th
                  style={{
                    ...styles.tableHeader,
                    textAlign: language === "ar" ? "right" : "left",
                  }}
                >
                  {t("employee")}
                </th>
              )}
              {showColumn("basicSalary") && (
                <th style={styles.tableHeader}>{t("basicSalary")}</th>
              )}
              {showColumn("allowances") && (
                <th style={styles.tableHeader}>{t("allowances")}</th>
              )}
              {showColumn("grossSalary") && (
                <th style={styles.tableHeader}>{t("grossSalary")}</th>
              )}
              {showColumn("deductions") && (
                <th style={styles.tableHeader}>{t("deductions")}</th>
              )}
              {showColumn("netSalary") && (
                <th style={styles.tableHeader}>{t("netSalary")}</th>
              )}
              {showColumn("status") && (
                <th style={styles.tableHeader}>{t("status")}</th>
              )}
              {showColumn("actions") && showRowActions && (
                <th style={{ ...styles.tableHeader, textAlign: "center" }}>
                  {t("actions")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayedPayroll.map((employee) => (
              <tr
                key={employee.id}
                style={{
                  background: currentTheme.colors.cardBackground,
                  borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              >
                {selectionEnabled && (
                  <td
                    style={{ ...styles.tableCell, width: "36px", padding: "0.75rem 0.5rem", textAlign: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      onClick={() => onToggleSelect?.(employee.id)}
                      role="checkbox"
                      aria-checked={selectedIds?.has(employee.id) || false}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onToggleSelect?.(employee.id); } }}
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        border: `2px solid ${selectedIds?.has(employee.id) ? currentTheme.colors.primary : (theme === "dark" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)")}`,
                        background: selectedIds?.has(employee.id) ? currentTheme.colors.primary : "transparent",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        flexShrink: 0,
                      }}
                    >
                      {selectedIds?.has(employee.id) && (
                        <Check size={10} strokeWidth={3} color={theme === "dark" ? "#0b1220" : "#ffffff"} />
                      )}
                    </span>
                  </td>
                )}
                {showColumn("employee") && (
                  <td style={styles.tableCell}>
                    <div style={{ display: "flex", alignItems: "Left", gap: "16px" }}>
                      <div>
                        <div
                          style={{
                            color: currentTheme.colors.textPrimary,
                            fontSize: "15px",
                            fontWeight: "700",
                            fontFamily: "monospace",
                            alignItems: "Left",
                            marginBottom: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {employee.employeeName}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {showColumn("basicSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.basicSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("allowances") && (
                  <td style={{ ...styles.tableCell, whiteSpace: "nowrap" }}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {`+${Math.round(Number(employee.totalAllowances) || 0).toLocaleString()} ${t("currency")}`}
                    </div>
                    <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "2px" }}>
                      {t("includingOvertime")}
                    </div>
                  </td>
                )}
                {showColumn("grossSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.grossSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("deductions") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.danger, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {`-${Math.round(employee.totalDeductions).toLocaleString()} ${t("currency")}`}
                    </div>
                    <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "2px" }}>
                      {t("taxInsuranceOther")}
                    </div>
                  </td>
                )}
                {showColumn("netSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.netSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("status") && (
                  <td style={styles.tableCell}>
                    <StatusBadge status={employee.status} language={language} t={t} />
                    {employee.paidDate && (
                      <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "4px" }}>
                        {new Date(employee.paidDate).toLocaleDateString("en-US")}
                      </div>
                    )}
                  </td>
                )}
                {showColumn("actions") && showRowActions && (
                  <td style={{ ...styles.tableCell, position: "relative", minWidth: "210px" }}>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                      {renderRowActionButtons(employee)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          theme={theme}
          isRTL={language === "ar"}
        />
      </div>
      )}
    </div>
  );
};

function btnStyle({ theme, variant, isIgnored, disabled }) {
  const isDark = theme === "dark";

  const variants = {
    neutral: {
      bg: isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)",
      color: isDark ? "#f1f5f9" : "#334155",
    },
    danger: {
      bg: isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
    success: {
      bg: isDark ? "rgba(16,185,129,0.25)" : "rgba(209,250,229,0.9)",
      color: isDark ? "#10b981" : "#059669",
    },
  };

  let v = variants[variant] || variants.neutral;
  if (isIgnored) v = variants.danger;

  return {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    background: v.bg,
    color: v.color,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
  };
}

function hoverIn(e, { theme, variant, isIgnored, disabled }) {
  if (disabled) return;
  const isDark = theme === "dark";
  const activeVariant = isIgnored ? "danger" : variant;

  if (activeVariant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.4)" : "rgba(254,202,202,0.9)";
  } else if (activeVariant === "success") {
    e.currentTarget.style.background = isDark ? "rgba(16,185,129,0.4)" : "rgba(167,243,208,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)";
  }
  e.currentTarget.style.transform = "translateY(-2px)";
}

function hoverOut(e, { theme, variant, isIgnored, disabled }) {
  if (disabled) return;
  const isDark = theme === "dark";
  const activeVariant = isIgnored ? "danger" : variant;

  if (activeVariant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)";
  } else if (activeVariant === "success") {
    e.currentTarget.style.background = isDark ? "rgba(16,185,129,0.25)" : "rgba(209,250,229,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)";
  }
  e.currentTarget.style.transform = "translateY(0)";
}

export default PayrollTable;
